import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Sparkles, Check, Upload, Loader2, FileText
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import { Input, Textarea, Button, Label, Card } from "../components/Primitives";
import { PROCESSES, MATERIALS } from "../data/taxonomy";
import { categories, aiImprovedText } from "../data/mockData";
import { createRequest } from "../lib/db";

const STEPS = ["step1", "step2", "step3", "step4"];

function parseBudget(str) {
  if (!str) return { min: null, max: null };
  const numbers = String(str).replace(/[^\d.,\-–]/g, "").split(/[-–]/).map((s) => Number(s.replace(/\./g, "").replace(",", "."))).filter((n) => !isNaN(n));
  if (numbers.length === 0) return { min: null, max: null };
  if (numbers.length === 1) return { min: numbers[0], max: numbers[0] };
  return { min: numbers[0], max: numbers[1] };
}

export default function NewRequest() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [aiLoading, setAiLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "Composites",
    quantity: 25,
    material: "Carbon T800",
    tolerance: "±0.1 mm",
    finish: "",
    budget: "€ 18.000 – 24.000",
    deadline: "12 settimane",
    location: "Milano, IT",
    requiredProcesses: [],
    requiredMaterials: [],
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const improveWithAI = () => {
    setAiLoading(true);
    setTimeout(() => {
      set("description", aiImprovedText[lang]);
      setAiLoading(false);
      toast.success(t("newRequest.aiApplied"));
    }, 1400);
  };

  const publish = async () => {
    if (!user?.id) {
      toast.error("You need to sign in first");
      return;
    }
    setPublishing(true);
    try {
      const { min, max } = parseBudget(form.budget);
      const { refCode } = await createRequest(user.id, {
        title: form.title || "Untitled request",
        description: form.description,
        category: form.category,
        material: form.material,
        quantity: form.quantity,
        budgetMin: min,
        budgetMax: max,
        deadline: form.deadline,
        location: form.location,
        tags: [form.category, form.material].filter(Boolean),
        requiredProcesses: form.requiredProcesses,
        requiredMaterials: form.requiredMaterials.length ? form.requiredMaterials : (form.material ? [form.material] : []),
        urgent: false,
      }, files);
      toast.success(t("newRequest.published"), { description: refCode });
      navigate("/app/requests");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Could not publish request");
    } finally {
      setPublishing(false);
    }
  };

  const onFilePick = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...picked]);
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto animate-fade-up">
      <div className="mb-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-electric-400 mb-2">
          R4Pro · Wizard
        </p>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t("newRequest.title")}</h1>
        <p className="text-ink-500 mt-1 text-sm">{t("newRequest.subtitle")}</p>
      </div>

      <div className="mb-8" data-testid="wizard-progress">
        <div className="flex gap-1.5 mb-3">
          {STEPS.map((s, i) => (
            <div key={s} className={`h-1 flex-1 rounded-full transition-all duration-500 ${i <= step ? "bg-electric-500" : "bg-carbon-600"}`} />
          ))}
        </div>
        <div className="flex justify-between text-[11px] font-mono uppercase tracking-wider">
          {STEPS.map((s, i) => (
            <span key={s} className={i === step ? "text-electric-400" : "text-ink-400"}>
              0{i + 1} · {t(`newRequest.${s}`)}
            </span>
          ))}
        </div>
      </div>

      <Card className="p-6 mb-6">
        {step === 0 && (
          <div className="space-y-5 animate-fade-up">
            <div>
              <h2 className="text-lg font-semibold mb-1">{t("newRequest.what")}</h2>
              <p className="text-xs text-ink-500">{t("newRequest.whatSub")}</p>
            </div>
            <div>
              <Label>{t("newRequest.titleField")}</Label>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder={t("newRequest.titlePlaceholder")} data-testid="wiz-title" />
            </div>
            <div>
              <Label>{t("newRequest.category")}</Label>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <button key={c} onClick={() => set("category", c)} data-testid={`wiz-cat-${c}`}
                    className={`px-3 py-1.5 rounded-md text-xs border transition ${
                      form.category === c
                        ? "border-electric-500 bg-electric-500/10 text-electric-400"
                        : "border-carbon-600 text-ink-600 hover:border-carbon-500 hover:text-ink-900"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>{t("newRequest.quantity")}</Label>
              <Input type="number" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="wiz-qty" />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5 animate-fade-up">
            <div>
              <h2 className="text-lg font-semibold mb-1">{t("newRequest.technical")}</h2>
              <p className="text-xs text-ink-500">{t("newRequest.technicalSub")}</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="!mb-0">{t("newRequest.description")}</Label>
                <button onClick={improveWithAI} disabled={aiLoading} data-testid="ai-improve-btn"
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-gradient-to-r from-electric-500 to-blue-600 text-ink-900 font-medium hover:opacity-90 transition disabled:opacity-60">
                  {aiLoading ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("newRequest.aiThinking")}</>
                  ) : (
                    <><Sparkles className="w-3.5 h-3.5" /> {t("newRequest.aiImprove")}</>
                  )}
                </button>
              </div>
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={t("newRequest.descPlaceholder")} className={aiLoading ? "shimmer" : ""} data-testid="wiz-description" />
            </div>

            {/* Required processes — feeds the matching engine */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="!mb-0">{t("newRequest.requiredProcesses")}</Label>
                <span className="text-[10px] font-mono text-ink-500">{form.requiredProcesses.length}</span>
              </div>
              <p className="text-[11px] text-ink-500 mb-2">{t("newRequest.requiredProcessesSub")}</p>
              <div className="flex flex-wrap gap-1.5">
                {PROCESSES.map((p) => {
                  const active = form.requiredProcesses.includes(p);
                  return (
                    <button key={p} type="button" data-testid={`wiz-proc-${p}`}
                      onClick={() => set("requiredProcesses", active
                        ? form.requiredProcesses.filter((x) => x !== p)
                        : [...form.requiredProcesses, p])}
                      className={`px-2.5 py-1 rounded-full text-[11px] border transition ${
                        active ? "border-electric-500 bg-electric-500/10 text-electric-600"
                               : "border-carbon-600 text-ink-700 hover:text-ink-900"
                      }`}>
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("newRequest.material")}</Label>
                <select value={form.material}
                  onChange={(e) => { set("material", e.target.value); set("requiredMaterials", e.target.value ? [e.target.value] : []); }}
                  data-testid="wiz-material"
                  className="w-full px-3 py-2 rounded-md bg-carbon-800 border border-carbon-600 text-sm text-ink-900 focus:border-electric-500 focus:outline-none">
                  <option value="">—</option>
                  {MATERIALS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <Label>{t("newRequest.tolerance")}</Label>
                <Input value={form.tolerance} onChange={(e) => set("tolerance", e.target.value)} data-testid="wiz-tolerance" />
              </div>
            </div>
            <div>
              <Label>{t("newRequest.files")}</Label>
              <label className="block border-2 border-dashed border-carbon-600 rounded-xl p-6 text-center hover:border-electric-500/50 transition cursor-pointer" data-testid="wiz-file-drop">
                <input type="file" multiple onChange={onFilePick} className="hidden" data-testid="wiz-file-input" />
                <Upload className="w-6 h-6 mx-auto text-ink-500 mb-2" />
                <div className="text-xs text-ink-500">{t("newRequest.dropFiles")}</div>
                <div className="text-[10px] font-mono text-ink-400 mt-1">STEP · IGES · PDF · DXF</div>
              </label>
              {files.length > 0 && (
                <ul className="mt-2 space-y-1" data-testid="wiz-file-list">
                  {files.map((f, i) => (
                    <li key={i} className="text-[11px] font-mono text-ink-700 flex items-center gap-2">
                      <FileText className="w-3 h-3" /> {f.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5 animate-fade-up">
            <div>
              <h2 className="text-lg font-semibold mb-1">{t("newRequest.conditions")}</h2>
              <p className="text-xs text-ink-500">{t("newRequest.conditionsSub")}</p>
            </div>
            <div>
              <Label>{t("newRequest.budget")}</Label>
              <Input value={form.budget} onChange={(e) => set("budget", e.target.value)} data-testid="wiz-budget" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("newRequest.deadline")}</Label>
                <Input value={form.deadline} onChange={(e) => set("deadline", e.target.value)} data-testid="wiz-deadline" />
              </div>
              <div>
                <Label>{t("newRequest.location")}</Label>
                <Input value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="wiz-location" />
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5 animate-fade-up">
            <div>
              <h2 className="text-lg font-semibold mb-1">{t("newRequest.review")}</h2>
              <p className="text-xs text-ink-500">{t("newRequest.reviewSub")}</p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                ["titleField", form.title || "—"],
                ["category", form.category],
                ["quantity", form.quantity],
                ["material", form.material],
                ["tolerance", form.tolerance],
                ["budget", form.budget],
                ["deadline", form.deadline],
                ["location", form.location],
              ].map(([k, v]) => (
                <div key={k} className="p-3 rounded-lg bg-carbon-900 border border-carbon-600">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mb-1">{t(`newRequest.${k}`)}</div>
                  <div className="text-sm text-ink-900 truncate">{v}</div>
                </div>
              ))}
            </div>
            {form.description && (
              <div className="p-4 rounded-lg bg-carbon-900 border border-carbon-600">
                <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mb-2 flex items-center gap-2">
                  <FileText className="w-3 h-3" /> {t("newRequest.description")}
                </div>
                <p className="text-xs text-ink-700 leading-relaxed">{form.description}</p>
              </div>
            )}
            {files.length > 0 && (
              <div className="p-4 rounded-lg bg-carbon-900 border border-carbon-600">
                <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mb-2">{t("newRequest.files")}</div>
                <ul className="space-y-1">
                  {files.map((f, i) => (
                    <li key={i} className="text-[11px] font-mono text-ink-700">{f.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>

      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
          data-testid="wiz-prev"
          className={step === 0 ? "opacity-40 pointer-events-none" : ""}>
          <ArrowLeft className="w-4 h-4" />
          {t("newRequest.previous")}
        </Button>
        {step < 3 ? (
          <Button onClick={() => setStep(step + 1)} data-testid="wiz-next">
            {t("newRequest.next")}
            <ArrowRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button onClick={publish} disabled={publishing} data-testid="wiz-publish">
            {publishing ? <><Loader2 className="w-4 h-4 animate-spin" /> …</> : <>{t("newRequest.publish")}<Check className="w-4 h-4" /></>}
          </Button>
        )}
      </div>
    </div>
  );
}
